import { httpInterceptor } from "@/core/services/middleware/HttpInterceptor";
import type { HttpClient } from "@/core/services/protocols/types";
import { IDefaultContabilResponse } from "../../types";
import type {
  IAuditoriaParams,
  ICabecalhoParams,
  ICabecalhoResponse,
  IDetalhesFormasTributacaoParams,
  IFaturamentoParams,
  IFaturamentoResponse,
  IFormasTributacaoParams,
  IGetAuditoriaResponse,
  IGetDashboardResponse,
  IGetDetalhesFormaTributacaoResponse,
  IGetHistoricoRegimeTributarioResponse,
  IObrigacoesFiscaisResponse,
  IObrigacoesParams,
  IQuadroSocietarioParams,
  IQuadroSocietarioResponse,
  IReceitaFederalResponse,
  ITributosAcumuladosParams,
  ITributosAcumuladosResponse,
  ITributosFaturamentoParams,
  ITributosFaturamentoResponse
} from "../requests/types";
import Auditoria from "./Auditoria";
import Certidoes from "./Certidoes";
import FormaTributacao from "./FormaTributacao";
import type { IDashboardGateway } from "./DashboardGateway";
import { IFormaTributacao } from "./FormaTributacao";
import QuadroSocietario from "./QuadroSocietario";
import ReceitaFederal from "./ReceitaFederal";
import ObrigacoesFiscais from "./ObrigacoesFiscais";
import TributosFaturamento from "./TributosFaturamento";
import TributosAcumulados from "./TributosAcumulados";
import Faturamento from "./Faturamento";
import Cabecalho from "./Cabecalho";
import { injectable, inject } from "inversify";
import "reflect-metadata";
import { DITypes } from "@/core/@types/types";
import DetalhesTributosRetidos, {
  IDetalhesTributosRetidosParams,
  IDetalhesTributosRetidosResponse
} from "./DetalhesTributosRetidos";
import DetalhesFormaTributacao from "./DetalhesFormaTributacao";
import Empecilhos, { IEmpecilhosResponse } from "./entity/Empecilhos";
import { diContainer } from "@/di";

@injectable()
export default class DashboardHttpGateway implements IDashboardGateway {
  @inject(DITypes.CONTABIL_HTTP_CLIENT)
  private httpClient!: HttpClient;

  protected response: IReceitaFederalResponse | undefined;

  setResponse(response: IReceitaFederalResponse | undefined) {
    this.response = response;
  }

  async getEmpecilhos(): Promise<Empecilhos> {
    const response = await this.httpClient.post<
      IDefaultContabilResponse<IEmpecilhosResponse>
    >({
      url: "v1/dashboard/geral/empecilhos",
      body: {
        empresasId: []
      }
    });

    const empecilhos = diContainer.get(Empecilhos);

    empecilhos.setResponse(response.body?.result);

    return empecilhos;
  }

  async getDetalhesFormaTributacao(
    params: IDetalhesFormasTributacaoParams
  ): Promise<DetalhesFormaTributacao> {
    const response = await this.httpClient.post<
      IDefaultContabilResponse<IGetDetalhesFormaTributacaoResponse>
    >({
      url: "v1/dashboard/geral/regime",
      body: {
        ...params
      }
    });

    return new DetalhesFormaTributacao(response.body?.result);
  }

  async getDetalhesTributosRetidos(
    params: IDetalhesTributosRetidosParams
  ): Promise<DetalhesTributosRetidos> {
    const response = await this.httpClient.post<
      IDefaultContabilResponse<IDetalhesTributosRetidosResponse[]>
    >({
      url: "/v1/dashboard/geral/detalhes-tributos",
      body: {
        ...params
      }
    });

    return new DetalhesTributosRetidos(response.body?.result);
  }

  async getFaturamento(params: IFaturamentoParams): Promise<Faturamento> {
    const response = await this.httpClient.post<
      IDefaultContabilResponse<IFaturamentoResponse>
    >({
      url: "/v1/dashboard/geral/faturamento",
      body: { ...params }
    });

    return new Faturamento(response.body?.result);
  }

  async getTributosAcumulados(
    params: ITributosAcumuladosParams
  ): Promise<TributosAcumulados> {
    const response = await this.httpClient.post<
      IDefaultContabilResponse<ITributosAcumuladosResponse>
    >({
      url: "/v1/dashboard/geral/tributos-acumulados",
      body: { ...params }
    });

    return new TributosAcumulados(response.body?.result);
  }

  async getTributosFaturamento(
    params: ITributosFaturamentoParams
  ): Promise<TributosFaturamento> {
    const response = await this.httpClient.post<
      IDefaultContabilResponse<ITributosFaturamentoResponse>
    >({
      url: "/v1/dashboard/geral/tributos-faturamento",
      body: { ...params }
    });

    return new TributosFaturamento(response.body?.result);
  }

  async getObrigacoes(params: IObrigacoesParams): Promise<ObrigacoesFiscais> {
    const response = await this.httpClient.post<
      IDefaultContabilResponse<IObrigacoesFiscaisResponse>
    >({
      url: "/v1/dashboard/geral/obrigacoes-fiscais",
      body: { ...params }
    });

    const obrigacoesFiscais = diContainer.get(ObrigacoesFiscais);

    obrigacoesFiscais.response = response.body?.result;

    return obrigacoesFiscais;
  }

  async getReceitaFederal(): Promise<ReceitaFederal> {
    const response = await this.httpClient.post<
      IDefaultContabilResponse<IReceitaFederalResponse>
    >({
      url: "/v1/dashboard/geral/receita-federal"
    });

    const receitaFederal = diContainer.get(ReceitaFederal);

    receitaFederal.setResponse(response.body?.result);

    return receitaFederal;
  }

  async getQuadroSocietario(
    params: IQuadroSocietarioParams
  ): Promise<QuadroSocietario> {
    const response = await this.httpClient.get<
      IDefaultContabilResponse<IQuadroSocietarioResponse>
    >({
      url: "v1/dashboard/geral/quadro-societario",
      queryParams: params
    });

    const quadroSocietario = new QuadroSocietario(response.body?.result);

    return quadroSocietario;
  }

  async getFormasTributacao(
    params: IFormasTributacaoParams
  ): Promise<IFormaTributacao> {
    const response = await this.httpClient.get<
      IDefaultContabilResponse<IGetHistoricoRegimeTributarioResponse>
    >({
      url: "v1/dashboard/geral/regime",
      queryParams: params
    });

    const formaTributacao = new FormaTributacao(response.body?.result);

    return formaTributacao.handler();
  }

  async getAuditoria(params: IAuditoriaParams): Promise<Auditoria> {
    const response = await this.httpClient.post<
      IDefaultContabilResponse<IGetAuditoriaResponse[]>
    >({
      url: "v1/dashboard/geral/avisos",
      body: {
        ...params
      }
    });

    const auditoria = new Auditoria(response.body?.result);

    return auditoria;
  }

  async getCabecalho(params: ICabecalhoParams): Promise<Cabecalho> {
    const response = await this.httpClient.post<
      IDefaultContabilResponse<ICabecalhoResponse>
    >({
      url: "/v1/dashboard/geral/cabecalho",
      body: { ...params }
    });

    const cabecalho = new Cabecalho(response.body?.result);

    return cabecalho;
  }

  @httpInterceptor()
  async getCertidoes(): Promise<Certidoes> {
    const response = await this.httpClient.get<IGetDashboardResponse>({
      url: "/certidoes"
    });

    const certidoes = new Certidoes(response.body);

    return certidoes;
  }
}
